import { ContentLayout } from "@pages/content-layout";
import { ProjectProfileHeader } from "@pages/project-profile/project-profile-header";
import { ProjectInfo } from "@pages/project-profile/tab-contents/project-info-tab/project-info";
import { LoadingScreen } from "@components/loading-screen";
import { useAppParams } from "@router/router-helper";
import { ProjectProfileTab } from "@router/route-params";
import { PageNotFound } from "@pages/page-not-found";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useEffect } from "react";
import { fetchProject } from "@store/project/project-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { projectStateSelector } from "@store/project/project-slice";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { PlansTable } from "@components/plans-table/plans-table";

/** Fetch project data and display the data in project profile page */
export function ProjectProfilePage(): React.JSX.Element {
  const { projectId, projectProfileTab } = useAppParams();
  const { fetchingProject, project } = useAppSelector(projectStateSelector);
  const dispatch = useAppDispatch();
  const { handleErrorWithPage } = useErrorHandler();

  // Fetches the project
  useEffect(() => {
    async function fetchProjectData(): Promise<void> {
      try {
        await dispatch(fetchProject({ projectId })).unwrap();
      } catch (error) {
        handleErrorWithPage({
          id: `ProjectProfilePage-${Date.now().toString()}`,
          title: "Could not load Project",
          error,
        });
      }
    }

    void fetchProjectData();
  }, [dispatch, projectId, handleErrorWithPage]);

  if (
    !projectProfileTab ||
    !Object.values(ProjectProfileTab).includes(projectProfileTab)
  ) {
    return <PageNotFound />;
  }

  return (
    <LoadingScreen
      isFetching={fetchingProject.selectedProject === "pending"}
      data={project}
    >
      {project && (
        <ContentLayout
          header={<ProjectProfileHeader project={project} />}
          tabs={[
            {
              label: "Information",
              route: ProjectProfileTab.projectInformation,
              content: <ProjectInfo project={project} />,
            },
            {
              label: "Plans",
              route: ProjectProfileTab.plans,
              content: (
                <PlansTable subjectType="PROJECT" subjectId={project.id} />
              ),
            },
          ]}
          selectedTab={projectProfileTab}
          trackingEventPageName={OpenTabEvents.openProjectProfileTabs}
        />
      )}
    </LoadingScreen>
  );
}
